import React, { useEffect, useState, useCallback, useMemo } from 'react';
import './img-spc-dynamic-table.scss';
import FmRadioButton from '../../../common/fm-button-radio/fm-radio-button/fm-radio-button';
import { ImgSpcResultsForDerivativeApiDataData } from '../../../../models/serice-price-calculator-model';
import {
    ImgSpcFieldLabelsContentFragment,
    ImgSpcHeadingContentFragment,
    ImgSpcResultingContentFragment,
    ImgSpcZaContentFragment,
} from '../../../../models/img-service-price-calculator/img-service-price-calculator';
import ServiceHandler from '../../../../services/service-handler';

interface HeaderRow {
    type: 'header';
    [key: string]: string;
}

interface TitleRow {
    type: 'title';
    title: string;
}

interface DataRow {
    type: 'data';
    [key: string]: any;
    description: string;
    brand?: string;
    brandOptions?: string[];
    unitUsed?: string;
    totalCost?: string | number;
    otherPartCost?: number | string;
    isTotal?: boolean;
    excludeCurrencySymbol?: boolean;
}

type TableRow = HeaderRow | TitleRow | DataRow;

interface ColumnDefinition {
    key: string;
    width: string;
    fixed?: boolean;
}

interface RenderDataRowParams {
    rowKey: string;
    dataRow: DataRow;
    rowIndex: number;
    columnDefinitions: ColumnDefinition[];
    firstColumnKey: string;
    validBrands: string[];
    selectedBrand: string;
    handleInternalRadioChange: (
        rowIndex: number,
        newBrandValue: string
    ) => void;
    getDisplayName: (brand: string) => string;
    formatWithThousandSeparators: (value: number) => string;
    getCurrencySymbol: () => string;
    currentAppCountry: string;
}

interface RenderTableRowsParams {
    rows: TableRow[];
    columnDefinitions: ColumnDefinition[];
    firstColumnKey: string;
    validBrands: string[];
    internalSelectedBrands: string[];
    handleInternalRadioChange: (
        rowIndex: number,
        newBrandValue: string
    ) => void;
    getDisplayName: (brand: string) => string;
    formatWithThousandSeparators: (value: number) => string;
    getCurrencySymbol: () => string;
    currentAppCountry: string;
}

interface GetDisplayValueParams {
    columnKey: string;
    dataRow: DataRow;
    selectedBrand: string;
    brandOptions?: string[];
    validBrands: string[];
    formatWithThousandSeparators: (value: number) => string;
    getCurrencySymbol: () => string;
    currentAppCountry: string;
}

interface PdfData {
    modelNames: string | string[];
    pdfPath?: string;
}

export interface DynamicTableSPCProps {
    columnDefinitions: ColumnDefinition[];
    imgSpcDerivativeData: ImgSpcResultsForDerivativeApiDataData | null;
    resultsLabel?: ImgSpcResultingContentFragment;
    fieldsLabel?: ImgSpcFieldLabelsContentFragment;
    currencyLabel?: ImgSpcZaContentFragment | null;
    headersLabel?: ImgSpcHeadingContentFragment;
    zaLabel?: ImgSpcZaContentFragment;
    selectedCountry: string;
    freeLaborCouponChecked: boolean;
    timingBeltChecked: boolean;
    iolmChecked: boolean;
    mechanicalPartsChecked: boolean;
    selectedMechanicalPartNumbers: string[];
    onlyIOLMChecked: boolean;
    oilChangeIOMLChecked: boolean;
    isInspectionDiscountApplicable: boolean;
}

const DynamicTableSPC = ({
    columnDefinitions,
    imgSpcDerivativeData,
    resultsLabel,
    fieldsLabel,
    currencyLabel,
    headersLabel,
    zaLabel,
    selectedCountry,
    freeLaborCouponChecked,
    timingBeltChecked,
    iolmChecked,
    mechanicalPartsChecked,
    selectedMechanicalPartNumbers,
    onlyIOLMChecked,
    oilChangeIOMLChecked,
    isInspectionDiscountApplicable,
}: DynamicTableSPCProps) => {
    const totalColumns = columnDefinitions.length;
    const [rows, setRows] = useState<TableRow[]>([]);
    const [internalSelectedBrands, setInternalSelectedBrands] = useState<
        string[]
    >([]);

    const currentAppCountry = useMemo(() => {
        return ServiceHandler.AppConfigurationService.current3LetterCountryCode;
    }, []);
    const currentLanguageRegionCode = useMemo(() => {
        return ServiceHandler.AppConfigurationService.currentLanguageRegionCode;
    }, []);

    const isThaiLanguage = useMemo(() => {
        return currentLanguageRegionCode.toUpperCase() === 'TH-TH';
    }, [currentLanguageRegionCode]);

    const { validBrands, getDisplayName } = useMemo(() => {
        const fordGenuine = `${fieldsLabel?.fordGenuine ?? ''}*`;
        const motorcraft = `${fieldsLabel?.motorcraft ?? ''}#`;

        const getDisplayNameFn = (brand: string) => {
            let result = brand;
            while (result.endsWith('*') || result.endsWith('#')) {
                result = result.slice(0, -1);
            }
            return result.trim();
        };
        return {
            validBrands: [fordGenuine, motorcraft],
            getDisplayName: getDisplayNameFn,
        };
    }, [fieldsLabel]);

    const getCurrencySymbol = useCallback((): string => {
        if (currentAppCountry.toUpperCase() === 'ZAF') {
            switch (selectedCountry) {
                case 'South Africa':
                    return currencyLabel?.currencyForSouthAfrica ?? '';
                case 'Namibia':
                    return currencyLabel?.currencyForNamibia ?? '';
                case 'Swaziland':
                    return currencyLabel?.currencyForSwaziland ?? '';
                default:
                    return '';
            }
        } else {
            return headersLabel?.currency ?? '';
        }
    }, [selectedCountry, currencyLabel, headersLabel, currentAppCountry]);

    const formatWithThousandSeparators = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const getServiceSchedulePdf = useCallback(() => {
        const derivativeName = imgSpcDerivativeData?.results?.[0]?.derivativeName
            ?.toLowerCase()
            .trim();
        const pdfData = zaLabel?.pathToImgSpcServiceSchedulePdfZa || [];

        if (!derivativeName || pdfData.length === 0) {
            return null;
        }

        const derivativeNameWithIOLMorSM = (): string => {
            if (onlyIOLMChecked) {
                return `${derivativeName} IOLM`.toLowerCase().trim();
            }
            if (!oilChangeIOMLChecked) {
                return `${derivativeName} SM`.toLowerCase().trim();
            }
            return derivativeName;
        };

        const normalizeModelNames = (
            modelNames: string | string[]
        ): string[] => {
            return Array.isArray(modelNames)
                ? modelNames.map(name => name.toLowerCase().trim())
                : [modelNames.toLowerCase().trim()];
        };

        const doesMatch = (
            normalizedModelNames: string[],
            targetName: string | any[]
        ): boolean => {
            return normalizedModelNames.some(name => name === targetName);
        };

        const findMatchingPdfData = (
            pdfData: PdfData[],
            derivativeNameWithIOLMorSM: string
        ): PdfData | undefined => {
            return pdfData.find(({ modelNames }) => {
                const normalizedModelNames = normalizeModelNames(modelNames);
                return doesMatch(
                    normalizedModelNames,
                    derivativeNameWithIOLMorSM
                );
            });
        };

        const match = findMatchingPdfData(
            pdfData,
            derivativeNameWithIOLMorSM()
        );

        if (match?.pdfPath) {
            const aemBaseUrl = process.env.REACT_APP_AEM_BASE_URL ?? '';
            return {
                pdfUrl: `${aemBaseUrl}${match.pdfPath}`,
                modelNames: Array.isArray(match.modelNames)
                    ? match.modelNames
                    : [match.modelNames],
                matchedItem: match,
            };
        }

        return null;
    }, [
        zaLabel?.pathToImgSpcServiceSchedulePdfZa,
        imgSpcDerivativeData?.results,
    ]);
    const serviceSchedulePdf = getServiceSchedulePdf();

    const transformApiResponseToRows = useCallback(
        (data: ImgSpcResultsForDerivativeApiDataData): TableRow[] => {
            const rows: TableRow[] = [];
            const duplicatePartNumbers = new Set<string>();
            const addHeaderRow = () => {
                rows.push({
                    type: 'header',
                    description: resultsLabel?.partsNameLabel ?? '',
                    brand: fieldsLabel?.brand ?? '',
                    unitUsed: resultsLabel?.unitUsedLabel ?? '',
                    totalCost: resultsLabel?.totalCostLabel ?? '',
                });
            };

            let vatRate = 0;
            const vatTaxFromLabel = resultsLabel?.vatTax;

            if (vatTaxFromLabel !== undefined && vatTaxFromLabel !== null) {
                if (typeof vatTaxFromLabel === 'number') {
                    vatRate = vatTaxFromLabel;
                } else if (typeof vatTaxFromLabel === 'string') {
                    const parsedVat = parseFloat(vatTaxFromLabel);
                    if (!isNaN(parsedVat)) {
                        vatRate = parsedVat;
                    }
                }
            }

            const buildBrandOptionsForPart = (part: any): string[] => {
                const partType = part.partType || part.PartType;
                if (!partType || partType === 'Other') return [];

                const hasOtherPartBrand =
                    part.otherPartCost != null && part.otherPartCost !== '';
                if (hasOtherPartBrand && validBrands.includes(partType)) {
                    return validBrands;
                } else {
                    return [partType];
                }
            };

            const addPartRows = (
                result: any,
                hasNonMisc1ScheduledParts: boolean
            ) => {
                result.parts?.forEach((part: any) => {
                    if (
                        part.unitsUsed &&
                        (hasNonMisc1ScheduledParts ||
                            (result.iolmJson?.iolmParts && iolmChecked)) &&
                        !duplicatePartNumbers.has(part.partNumber)
                    ) {
                        duplicatePartNumbers.add(part.partNumber);
                        rows.push({
                            type: 'data',
                            description:
                                isThaiLanguage && part.partNameOL
                                    ? part.partNameOL
                                    : part.partName,
                            brand:
                                !part.partType || part.partType === 'Other'
                                    ? ''
                                    : part.partType,
                            brandOptions: buildBrandOptionsForPart(part),
                            unitUsed: part.unitsUsed?.toString() || '',
                            totalCost:
                                part.totalrepaircost !== undefined
                                    ? String(part.totalrepaircost)
                                    : '',
                            otherPartCost: part.otherPartCost,
                            otherTotalrepaircost: part.otherTotalrepaircost,
                            isTotal: false,
                        });
                    }
                });
            };

            const addIOLMRows = (result: any) => {
                const shouldSkipPart = (iolmPart: any): boolean => {
                    return (
                        iolmChecked &&
                        iolmPart.PartNumber?.toLocaleUpperCase() === 'MISC1'
                    );
                };

                const getDescription = (iolmPart: any): string => {
                    return isThaiLanguage && iolmPart.PartNameThai
                        ? iolmPart.PartNameThai
                        : iolmPart.PartName + '*';
                };

                const getBrand = (iolmPart: any): string => {
                    return !iolmPart.PartType || iolmPart.PartType === 'Other'
                        ? ''
                        : iolmPart.PartType;
                };

                const getMotocraftPartCost = (
                    iolmPart: any
                ): number | undefined => {
                    return iolmPart.TotalCost || iolmPart.PartCostPerUnit;
                };

                const getOtherPartCost = (
                    iolmPart: any
                ): number | undefined => {
                    return iolmPart.PartType === validBrands[1]
                        ? iolmPart.PartCostPerUnit
                        : undefined;
                };

                result.iolmJson?.iolmParts?.forEach((iolmPart: any) => {
                    if (
                        !duplicatePartNumbers.has(iolmPart.PartNumber) &&
                        !shouldSkipPart(iolmPart)
                    ) {
                        duplicatePartNumbers.add(iolmPart.PartNumber);

                        const isMotocraft =
                            iolmPart.PartType === validBrands[1];
                        const partCost = isMotocraft
                            ? getMotocraftPartCost(iolmPart)
                            : getOtherPartCost(iolmPart);
                        rows.push({
                            type: 'data',
                            description: getDescription(iolmPart),
                            brand: getBrand(iolmPart),
                            brandOptions: buildBrandOptionsForPart(iolmPart),
                            unitUsed: iolmPart.Quantity?.toString() || '',
                            totalCost:
                                iolmPart.TotalCost !== undefined
                                    ? String(iolmPart.TotalCost)
                                    : '',
                            isTotal: false,
                            isMotocraft,
                            otherPartCost: partCost,
                        });
                    }
                });
            };

            const addTimingBeltRows = (timingBeltPartDetails: any[]) => {
                timingBeltPartDetails.forEach(timingBeltPart => {
                    rows.push({
                        type: 'data',
                        description:
                            isThaiLanguage && timingBeltPart.PartNameThai
                                ? timingBeltPart.PartNameThai
                                : timingBeltPart.PartName,
                        unitUsed: '1.00',
                        totalCost:
                            timingBeltPart.PartCost !== undefined
                                ? String(timingBeltPart.PartCost)
                                : '',
                        otherPartCost: timingBeltPart.PartCost,
                        isTotal: false,
                    });
                });
            };

            const addMechanicalPartsRows = (
                mechanicalParts: any[],
                selectedPartNumbersSet: Set<string>
            ) => {
                const duplicatePartNumbers = new Set<string>();
                mechanicalParts.forEach(category => {
                    const categoryName = getCategoryName(category);
                    const filteredParts = filterSelectedParts(
                        category.partsdetails,
                        selectedPartNumbersSet
                    );

                    processFilteredParts(
                        filteredParts,
                        categoryName,
                        duplicatePartNumbers
                    );
                });
            };

            const processFilteredParts = (
                filteredParts: any[],
                categoryName: string,
                duplicatePartNumbers: Set<string>
            ) => {
                filteredParts.forEach(partDetail => {
                    if (!duplicatePartNumbers.has(partDetail.PartNumber)) {
                        duplicatePartNumbers.add(partDetail.PartNumber);
                        const row = createMechanicalPartRow(
                            categoryName,
                            partDetail
                        );
                        rows.push(row);
                    }
                });
            };

            const createMechanicalPartRow = (
                categoryName: string,
                partDetail: any
            ): TableRow => {
                const description = buildDescription(categoryName, partDetail);
                const brandName = buildBrandName(partDetail.FordandMotorcraft);
                return buildRow(description, brandName, partDetail);
            };

            const getCategoryName = (category: any): string => {
                return isThaiLanguage && category.categoryinthai
                    ? category.categoryinthai
                    : category.category;
            };

            const filterSelectedParts = (
                partsDetails: any[],
                selectedPartNumbersSet: Set<string>
            ): any[] => {
                return partsDetails.filter(partDetail =>
                    selectedPartNumbersSet.has(partDetail.PartNumber)
                );
            };

            const buildDescription = (
                categoryName: string,
                partDetail: any
            ): string => {
                const partName =
                    isThaiLanguage && partDetail.PartNameThai
                        ? partDetail.PartNameThai
                        : partDetail.PartName;

                const partNameWithModelYear =
                    partDetail.ModelYear &&
                    partDetail.ModelYear.toLowerCase() !== 'null'
                        ? `${partName} (${partDetail.ModelYear})`
                        : partName;

                return `${partNameWithModelYear}`.trim();
            };

            const buildBrandName = (brand: string | undefined): string => {
                return brand ? `(${getDisplayName(brand)})` : '';
            };

            const buildRow = (
                description: string,
                brandName: string,
                partDetail: any
            ): TableRow => {
                return {
                    type: 'data',
                    description: `${description} ${brandName}`.trim(),
                    brand: partDetail.FordandMotorcraft || '',
                    brandOptions: buildBrandOptionsForPart(
                        partDetail.FordandMotorcraft
                    ),
                    unitUsed: '1.00',
                    totalCost:
                        partDetail.PartCost !== undefined
                            ? String(partDetail.PartCost)
                            : '',
                    otherPartCost: partDetail.PartCost,
                    isTotal: false,
                };
            };

            const calculatePartsTotals = (rows: TableRow[]) => {
                const parseCost = (cost: string | number): number => {
                    if (typeof cost === 'number') return cost;
                    if (typeof cost === 'string') {
                        if (cost.toLowerCase() === 'free') return 0;
                        const parsedCost = parseFloat(cost);
                        return isNaN(parsedCost) ? 0 : parsedCost;
                    }
                    return 0;
                };

                const partsTotal = rows.reduce((total, row) => {
                    if (row.type === 'data' && !row.isTotal && row.totalCost) {
                        const cost = parseCost(row.totalCost);
                        return total + cost;
                    }
                    return total;
                }, 0);

                const partsWithVat = partsTotal * (1 + vatRate / 100);
                return { excludingVAT: partsTotal, includingVAT: partsWithVat };
            };

            const addPartsTotalRows = (partsTotals: {
                excludingVAT: number;
                includingVAT: number;
            }) => {
                rows.push({
                    type: 'data',
                    description:
                        resultsLabel?.totalPartsCostExcludingVATLabel ?? '',
                    brand: '',
                    brandOptions: undefined,
                    unitUsed: '',
                    totalCost: partsTotals.excludingVAT.toFixed(2),
                    isTotal: true,
                });
                rows.push({
                    type: 'data',
                    description:
                        currentAppCountry.toUpperCase() === 'VNM'
                            ? ''
                            : resultsLabel?.totalPartsCostIncludingVATLabel ??
                              '',
                    brand: '',
                    brandOptions: undefined,
                    unitUsed: '',
                    totalCost:
                        currentAppCountry.toUpperCase() === 'VNM'
                            ? ''
                            : partsTotals.includingVAT.toFixed(2),
                    isTotal: true,
                });
            };

            const calculateTimingBeltHours = (
                timingBeltPartDetails: any[]
            ): number => {
                return timingBeltPartDetails.reduce((total, timingBeltPart) => {
                    return (
                        total +
                        (parseFloat(timingBeltPart.LabourHours || '0') || 0)
                    );
                }, 0);
            };

            const calculateLaborHoursForPartDetails = (
                partsDetails: any[],
                selectedPartNumbersSet: Set<string>
            ): number => {
                return partsDetails.reduce((total: number, partDetail: any) => {
                    const laborHours =
                        parseFloat(partDetail.laborHours || '0') || 0;
                    return selectedPartNumbersSet.has(partDetail.PartNumber)
                        ? total + laborHours
                        : total;
                }, 0);
            };

            const calculateLaborHoursForCategory = (
                category: any,
                selectedPartNumbersSet: Set<string>
            ): number => {
                return calculateLaborHoursForPartDetails(
                    category.partsdetails,
                    selectedPartNumbersSet
                );
            };

            const calculateTotalLaborHours = (
                mechanicalParts: any[],
                selectedPartNumbersSet: Set<string>
            ): number => {
                return mechanicalParts.reduce(
                    (totalLaborHours: number, category: any) =>
                        totalLaborHours +
                        calculateLaborHoursForCategory(
                            category,
                            selectedPartNumbersSet
                        ),
                    0
                );
            };

            const calculateMechanicalPartsLaborHours = (
                currentResult: any,
                selectedPartNumbersSet: Set<string>
            ): number => {
                if (
                    !currentResult?.mechanicalParts ||
                    !Array.isArray(currentResult.mechanicalParts)
                ) {
                    return 0;
                }

                return calculateTotalLaborHours(
                    currentResult.mechanicalParts,
                    selectedPartNumbersSet
                );
            };

            const calculateLaborHours = (
                currentResult: any,
                timingBeltPartDetails?: any[]
            ) => {
                let timingBeltHours = 0;
                let mechanicalPartsLaborHours = 0;

                if (
                    timingBeltChecked &&
                    timingBeltPartDetails &&
                    Array.isArray(timingBeltPartDetails)
                ) {
                    timingBeltHours = calculateTimingBeltHours(
                        timingBeltPartDetails
                    );
                }

                if (
                    mechanicalPartsChecked &&
                    selectedMechanicalPartNumbers &&
                    selectedMechanicalPartNumbers.length > 0
                ) {
                    const selectedPartNumbersSet = new Set(
                        selectedMechanicalPartNumbers
                    );
                    mechanicalPartsLaborHours = calculateMechanicalPartsLaborHours(
                        currentResult,
                        selectedPartNumbersSet
                    );
                }

                return { timingBeltHours, mechanicalPartsLaborHours };
            };

            const calculateLaborCosts = (
                currentResult: any,
                timingBeltHours: number,
                mechanicalPartsLaborHours: number
            ) => {
                const getLaborRatePerHour = (result: any): number => {
                    if (result?.dealerLabourCharges) {
                        return typeof result.dealerLabourCharges === 'number'
                            ? result.dealerLabourCharges
                            : parseFloat(result.dealerLabourCharges) || 0;
                    }
                    if (result?.labourcostperHour) {
                        return typeof result.labourcostperHour === 'number'
                            ? result.labourcostperHour
                            : parseFloat(result.labourcostperHour) || 0;
                    }
                    return 0;
                };

                const calculateScheduledMaintHours = (
                    result: any,
                    timingBeltHours: number
                ): number => {
                    const maintananceLaborHours =
                        typeof result.labourcostperHour === 'number'
                            ? result.labourcostperHour
                            : parseFloat(result.labourcostperHour) || 0;

                    if (result?.labourcostperHour) {
                        return timingBeltChecked && timingBeltHours > 0
                            ? timingBeltHours + maintananceLaborHours
                            : maintananceLaborHours;
                    }
                    return timingBeltHours;
                };

                const getIolmHours = (iolmJson: any): number => {
                    return parseFloat(iolmJson?.laborHours) || 0;
                };

                const isScheduledLaborFree = (): boolean => {
                    return (
                        (currentAppCountry.toUpperCase() === 'PHL' &&
                            isInspectionDiscountApplicable) ||
                        imgSpcDerivativeData?.results[0].labourApplicable?.toUpperCase() !==
                            'YES'
                    );
                };

                const calculateLaborCost = (
                    hours: number,
                    rate: number,
                    isFree: boolean
                ): number => (isFree ? 0 : hours * rate);

                const laborRatePerHour = getLaborRatePerHour(currentResult);
                const scheduledMaintHours = calculateScheduledMaintHours(
                    currentResult,
                    timingBeltHours
                );
                const iolmJson = currentResult?.iolmJson || {};
                const iolmHours = getIolmHours(iolmJson);
                const scheduledLaborCost = calculateLaborCost(
                    scheduledMaintHours,
                    laborRatePerHour,
                    isScheduledLaborFree()
                );

                const iolmLaborRate = freeLaborCouponChecked
                    ? 0
                    : laborRatePerHour;
                const iolmLaborCost =
                    iolmChecked || onlyIOLMChecked
                        ? iolmHours * iolmLaborRate
                        : 0;

                const mechanicalPartsLaborCost =
                    mechanicalPartsLaborHours * laborRatePerHour;

                const shouldIncludeIOLM =
                    onlyIOLMChecked || (iolmChecked && oilChangeIOMLChecked);

                let totalLaborCost = 0;
                if (onlyIOLMChecked) {
                    totalLaborCost = iolmLaborCost;
                } else {
                    const includeIOLM = shouldIncludeIOLM ? iolmLaborCost : 0;
                    totalLaborCost =
                        scheduledLaborCost +
                        includeIOLM +
                        mechanicalPartsLaborCost;
                }

                const totalLaborCostWithVat =
                    totalLaborCost * (1 + vatRate / 100);

                return {
                    laborRatePerHour,
                    scheduledMaintHours,
                    scheduledLaborCost,
                    iolmHours,
                    iolmLaborCost,
                    mechanicalPartsLaborCost,
                    totalLaborCost,
                    totalLaborCostWithVat,
                    hasIolmData: Object.keys(iolmJson).length > 0,
                };
            };

            const addLaborSection = (currentResult: any, laborCosts: any) => {
                const {
                    laborRatePerHour,
                    scheduledMaintHours,
                    scheduledLaborCost,
                    iolmHours,
                    iolmLaborCost,
                    mechanicalPartsLaborCost,
                    totalLaborCost,
                    totalLaborCostWithVat,
                    hasIolmData,
                } = laborCosts;

                const { mechanicalPartsLaborHours } = calculateLaborHours(
                    currentResult,
                    currentResult?.timingBeltPartDetails
                );

                rows.push({
                    type: 'title',
                    title: resultsLabel?.totalLabourLabel ?? '',
                });

                const formatRateAndSymbol = (rate: number) => {
                    const formattedRate = formatWithThousandSeparators(rate);
                    const currencySymbol = getCurrencySymbol() + ' ';
                    const perHourLabel =
                        resultsLabel?.totalLabourExpensePerHourLabel;

                    return currentAppCountry.toUpperCase() === 'THA'
                        ? `${formattedRate} ${currencySymbol} ${perHourLabel}`
                        : `${currencySymbol}${formattedRate} ${perHourLabel}`;
                };

                const addLaborRow = (
                    description: string,
                    totalCost: number | string,
                    excludeCurrencySymbol = false,
                    isTotal = false
                ) => {
                    rows.push({
                        type: 'data',
                        description,
                        brand: '',
                        brandOptions: undefined,
                        unitUsed: '',
                        totalCost:
                            typeof totalCost === 'number'
                                ? totalCost.toFixed(2)
                                : totalCost,
                        excludeCurrencySymbol,
                        isTotal,
                    });
                };

                const addConditionalLaborRows = () => {
                    if (!onlyIOLMChecked && currentResult?.parts?.length > 0) {
                        addLaborRow(
                            `${resultsLabel?.totalLabourHoursLabelIOLM}${resultsLabel?.totalLabourDisclaimerSymbol} ${resultsLabel?.scheduledMaintenanceDisclaimerLabel}`,
                            scheduledMaintHours,
                            true
                        );

                        addLaborRow(
                            `${
                                resultsLabel?.totalLabourExpenseLabelIOLM
                            } (${formatRateAndSymbol(laborRatePerHour)}) ${
                                resultsLabel?.scheduledMaintenanceDisclaimerLabel
                            }`,
                            scheduledLaborCost > 0 ? scheduledLaborCost : 'Free'
                        );
                    }

                    if (
                        (onlyIOLMChecked ||
                            (iolmChecked && oilChangeIOMLChecked)) &&
                        hasIolmData &&
                        iolmHours > 0
                    ) {
                        addLaborRow(
                            `${resultsLabel?.totalLabourHoursLabelIOLM}${resultsLabel?.totalLabourDisclaimerSymbol} ${resultsLabel?.iolmDisclaimerLabel}${resultsLabel?.totalLabourDisclaimerIOLMSymbol}`,
                            iolmHours,
                            true
                        );

                        addLaborRow(
                            `${
                                resultsLabel?.totalLabourExpenseLabelIOLM
                            } (${formatRateAndSymbol(laborRatePerHour)}) ${
                                resultsLabel?.iolmDisclaimerLabel
                            }${resultsLabel?.totalLabourDisclaimerIOLMSymbol}`,
                            !freeLaborCouponChecked && iolmLaborCost > 0
                                ? iolmLaborCost
                                : 'Free'
                        );
                    }

                    if (mechanicalPartsChecked) {
                        addLaborRow(
                            `${resultsLabel?.totalLabourHoursLabelIOLM ??
                                ''}${resultsLabel?.totalLabourDisclaimerSymbol ??
                                ''} ${resultsLabel?.mechanicalDisclaimerLabel}`,
                            mechanicalPartsLaborHours,
                            true
                        );

                        addLaborRow(
                            `${resultsLabel?.totalLabourExpenseLabelIOLM ??
                                ''} (${formatRateAndSymbol(
                                laborRatePerHour
                            )}) ${resultsLabel?.mechanicalDisclaimerLabel}`,
                            mechanicalPartsLaborCost > 0
                                ? mechanicalPartsLaborCost
                                : '0.00'
                        );
                    }
                };

                const addTotalLaborRows = () => {
                    addLaborRow(
                        resultsLabel?.totalLabourCostExcludingVATLabel ?? '',
                        totalLaborCost > 0 ? totalLaborCost : 'Free',
                        false,
                        true
                    );

                    if (currentAppCountry.toUpperCase() !== 'VNM') {
                        const calculatedTotalCost =
                            totalLaborCostWithVat > 0
                                ? totalLaborCostWithVat.toFixed(2)
                                : 'Free';

                        addLaborRow(
                            resultsLabel?.totalLabourCostIncludingVATLabel ??
                                '',
                            calculatedTotalCost,
                            false,
                            true
                        );
                    }
                };

                addConditionalLaborRows();
                addTotalLaborRows();
            };

            const addGrandTotalSection = (
                partsTotals: { excludingVAT: number; includingVAT: number },
                laborCosts: any
            ) => {
                const { totalLaborCost, totalLaborCostWithVat } = laborCosts;

                if (!data.totals) {
                    const totalCostExcludingVat =
                        partsTotals.excludingVAT + totalLaborCost;
                    const totalCostIncludingVat =
                        partsTotals.includingVAT + totalLaborCostWithVat;

                    rows.push({
                        type: 'title',
                        title: resultsLabel?.grandTotalLabel ?? '',
                    });
                    rows.push({
                        type: 'data',
                        description:
                            resultsLabel?.totalPartsAndLabourCostLabel ?? '',
                        brand: '',
                        brandOptions: undefined,
                        unitUsed: '',
                        totalCost: totalCostExcludingVat.toFixed(2),
                        isTotal: true,
                    });
                    rows.push({
                        type: 'data',
                        description:
                            currentAppCountry.toUpperCase() === 'VNM'
                                ? ''
                                : resultsLabel?.totalPartsAndLabourCostLabelIncludingVAT ??
                                  '',
                        brand: '',
                        brandOptions: undefined,
                        unitUsed: '',
                        totalCost:
                            currentAppCountry.toUpperCase() === 'VNM'
                                ? ''
                                : totalCostIncludingVat.toFixed(2),
                        isTotal: true,
                    });
                }
            };

            addHeaderRow();
            const currentResult = data.results[0];
            const hasNonMisc1ScheduledParts = currentResult?.parts?.some(
                part => part.partNumber?.toLocaleUpperCase() !== 'MISC1'
            );

            data.results.forEach(result => {
                if (!onlyIOLMChecked)
                    addPartRows(result, !!hasNonMisc1ScheduledParts);
                if (
                    (onlyIOLMChecked ||
                        (oilChangeIOMLChecked && iolmChecked)) &&
                    result.iolmJson?.iolmParts
                )
                    addIOLMRows(result);
            });

            if (timingBeltChecked && currentResult?.timingBeltPartDetails)
                addTimingBeltRows(currentResult.timingBeltPartDetails);

            if (
                mechanicalPartsChecked &&
                selectedMechanicalPartNumbers.length > 0 &&
                currentResult?.mechanicalParts
            ) {
                const selectedPartNumbersSet = new Set(
                    selectedMechanicalPartNumbers
                );
                addMechanicalPartsRows(
                    currentResult.mechanicalParts,
                    selectedPartNumbersSet
                );
            }

            const partsTotals = calculatePartsTotals(rows);
            addPartsTotalRows(partsTotals);

            const {
                timingBeltHours,
                mechanicalPartsLaborHours,
            } = calculateLaborHours(
                currentResult,
                currentResult?.timingBeltPartDetails
            );
            const laborCosts = calculateLaborCosts(
                currentResult,
                timingBeltHours,
                mechanicalPartsLaborHours
            );
            addLaborSection(currentResult, laborCosts);

            addGrandTotalSection(partsTotals, laborCosts);

            return rows;
        },
        [
            resultsLabel,
            fieldsLabel,
            validBrands,
            getDisplayName,
            imgSpcDerivativeData?.results,
            freeLaborCouponChecked,
            timingBeltChecked,
            mechanicalPartsChecked,
            selectedMechanicalPartNumbers,
            getCurrencySymbol,
            currentAppCountry,
            onlyIOLMChecked,
            oilChangeIOMLChecked,
            iolmChecked,
            isInspectionDiscountApplicable,
            isThaiLanguage,
        ]
    );

    useEffect(() => {
        if (imgSpcDerivativeData) {
            const transformedRows = transformApiResponseToRows(
                imgSpcDerivativeData
            );
            setRows(transformedRows);
            setInternalSelectedBrands(
                transformedRows.map(row =>
                    row.type === 'data' ? String(row.brand ?? '') : ''
                )
            );
        } else {
            setRows([]);
            setInternalSelectedBrands([]);
        }
    }, [imgSpcDerivativeData, transformApiResponseToRows]);

    if (totalColumns === 0 || rows.length === 0) return null;

    const firstColumnKey = columnDefinitions[0].key;

    const handleInternalRadioChange = (
        rowIndex: number,
        newBrandValue: string
    ) => {
        const updatedSelectedBrands = [...internalSelectedBrands];
        updatedSelectedBrands[rowIndex] = newBrandValue;
        setInternalSelectedBrands(updatedSelectedBrands);

        const updatedRows = [...rows];
        let partsTotal = 0;
        let laborTotal = 0;
        let laborTotalWithVat = 0;

        const vatRate =
            typeof resultsLabel?.vatTax === 'number'
                ? resultsLabel.vatTax
                : parseFloat(String(resultsLabel?.vatTax));

        let laborSectionStartIndex = -1;

        const parseCost = (cost: string | number): number => {
            if (typeof cost === 'number') {
                return cost;
            }
            if (typeof cost === 'string') {
                if (cost.toLowerCase() === 'free') {
                    return 0;
                }
                return parseFloat(cost) || 0;
            }
            return 0;
        };

        const calculateBrandCost = (
            dataRow: DataRow,
            selectedBrand: string,
            validBrands: string[]
        ): number => {
            if (selectedBrand === validBrands[0]) {
                return parseCost(dataRow.totalCost ?? 0);
            } else if (selectedBrand === validBrands[1]) {
                return parseCost(dataRow.otherPartCost ?? 0);
            }
            return 0;
        };

        const calculateCost = (
            dataRow: DataRow,
            selectedBrand: string
        ): number => {
            if (dataRow.brandOptions && dataRow.brandOptions.length > 0) {
                return calculateBrandCost(dataRow, selectedBrand, validBrands);
            }
            return parseCost(dataRow.totalCost ?? 0);
        };

        const updateTotals = (dataRow: DataRow) => {
            const parseTotalCost = (
                totalCost: string | number | undefined
            ): number => {
                if (typeof totalCost === 'string') {
                    return totalCost.toLowerCase() === 'free'
                        ? 0
                        : parseFloat(totalCost) || 0;
                }
                if (typeof totalCost === 'number') {
                    return totalCost;
                }
                return 0;
            };

            if (
                dataRow.description ===
                resultsLabel?.totalLabourCostExcludingVATLabel
            ) {
                laborTotal = parseTotalCost(dataRow.totalCost);
            } else if (
                dataRow.description ===
                resultsLabel?.totalLabourCostIncludingVATLabel
            ) {
                laborTotalWithVat = parseTotalCost(dataRow.totalCost);
            }
        };

        const updateRowTotals = () => {
            const partsWithVat = partsTotal * (1 + vatRate / 100);
            const totalCostExcludingVat = partsTotal + laborTotal;
            const totalCostIncludingVat = partsWithVat + laborTotalWithVat;

            updatedRows.forEach((row, index) => {
                if (row.type === 'data' && row.isTotal) {
                    const dataRow = row;
                    if (
                        dataRow.description ===
                        resultsLabel?.totalPartsCostExcludingVATLabel
                    ) {
                        updatedRows[index] = {
                            ...dataRow,
                            totalCost: partsTotal.toFixed(2),
                        };
                    } else if (
                        dataRow.description ===
                        resultsLabel?.totalPartsCostIncludingVATLabel
                    ) {
                        updatedRows[index] = {
                            ...dataRow,
                            totalCost: partsWithVat.toFixed(2),
                        };
                    } else if (
                        dataRow.description ===
                        resultsLabel?.totalPartsAndLabourCostLabel
                    ) {
                        updatedRows[index] = {
                            ...dataRow,
                            totalCost: totalCostExcludingVat.toFixed(2),
                        };
                    } else if (
                        dataRow.description ===
                        resultsLabel?.totalPartsAndLabourCostLabelIncludingVAT
                    ) {
                        updatedRows[index] = {
                            ...dataRow,
                            totalCost: totalCostIncludingVat.toFixed(2),
                        };
                    }
                }
            });
        };

        updatedRows.forEach((row, index) => {
            if (row.type === 'title') {
                if (row.title === resultsLabel?.totalLabourLabel) {
                    laborSectionStartIndex = index;
                }
            }
        });

        updatedRows.forEach((row, index) => {
            if (row.type === 'data' && !row.isTotal) {
                const isPartRow =
                    laborSectionStartIndex === -1 ||
                    index < laborSectionStartIndex;
                if (isPartRow) {
                    const selectedBrand = updatedSelectedBrands[index] || '';
                    const cost = calculateCost(row, selectedBrand);
                    if (!isNaN(cost) && cost > 0) {
                        partsTotal += cost;
                    }
                }
            }

            if (row.type === 'data' && row.isTotal) {
                updateTotals(row);
            }
        });

        updateRowTotals();
        setRows(updatedRows);
    };

    const renderHeaderRow = (
        rowKey: string,
        headerRow: HeaderRow,
        columnDefinitions: ColumnDefinition[],
        firstColumnKey: string
    ) => {
        return (
            <tr key={rowKey} className="fm-spc-header-row">
                {columnDefinitions.map(colDef => {
                    const isFirstColumn = colDef.key === firstColumnKey;
                    const cellClasses = isFirstColumn
                        ? 'fm-spc-fixed-column'
                        : '';
                    return (
                        <th key={colDef.key} className={cellClasses}>
                            {headerRow[colDef.key]}
                        </th>
                    );
                })}
            </tr>
        );
    };

    const renderTitleRow = (
        rowKey: string,
        titleRow: TitleRow,
        columnDefinitions: ColumnDefinition[]
    ) => {
        return (
            <tr key={rowKey} className="fm-spc-section-title">
                {columnDefinitions.map((colDef, colIndex) => (
                    <td
                        key={colDef.key}
                        className={colIndex === 0 ? 'fm-spc-fixed-column' : ''}
                    >
                        {colIndex === 0 ? titleRow.title : ''}
                    </td>
                ))}
            </tr>
        );
    };

    const renderBrandColumn = (
        dataRow: DataRow,
        rowIndex: number,
        brandOptions: string[] | undefined,
        validBrands: string[],
        selectedBrand: string,
        handleInternalRadioChange: (
            rowIndex: number,
            newBrandValue: string
        ) => void,
        getDisplayName: (brand: string) => string
    ) => {
        if (!brandOptions || brandOptions.length === 0) {
            return <td>{dataRow.brand ?? ''}</td>;
        }

        if (brandOptions.length === 1) {
            const singleBrand = brandOptions[0];
            return <td>{getDisplayName(singleBrand)}</td>;
        }

        return (
            <td className="fm-spc-brand-cell-radio">
                {validBrands.map((option, optionIndex) => {
                    const isSelected = option === selectedBrand;
                    const shouldShowOption = brandOptions.includes(option);
                    if (!shouldShowOption) return null;

                    const isOptionDisabled = isBrandOptionDisabled(
                        dataRow,
                        option,
                        validBrands
                    );
                    if (isOptionDisabled) return null;

                    return (
                        <div
                            key={`brand-option-${rowIndex}-${optionIndex}`}
                            className="fm-spc-brand-option"
                        >
                            <FmRadioButton
                                label={getDisplayName(option)}
                                value={option}
                                name={`brand-${rowIndex}`}
                                checked={isSelected}
                                onChange={() =>
                                    handleInternalRadioChange(rowIndex, option)
                                }
                            />
                        </div>
                    );
                })}
            </td>
        );
    };

    const isBrandOptionDisabled = (
        dataRow: DataRow,
        option: string,
        validBrands: string[]
    ) => {
        const isFordGenuine = option === validBrands[0];
        const isMotorcraft = option === validBrands[1];

        if (dataRow.brand === validBrands[0]) {
            return (
                isMotorcraft &&
                (dataRow.otherPartCost == null || dataRow.otherPartCost === '')
            );
        } else if (dataRow.brand === validBrands[1] || dataRow.isMotocraft) {
            return isFordGenuine;
        }

        return false;
    };

    const renderDataRow = ({
        rowKey,
        dataRow,
        rowIndex,
        columnDefinitions,
        firstColumnKey,
        validBrands,
        selectedBrand,
        handleInternalRadioChange,
        getDisplayName,
        formatWithThousandSeparators,
        getCurrencySymbol,
        currentAppCountry,
    }: RenderDataRowParams) => {
        const isTotalRow = dataRow.isTotal === true;
        const rowClasses = isTotalRow ? 'fm-spc-total-row' : '';
        const brandOptions = Array.isArray(dataRow.brandOptions)
            ? dataRow.brandOptions
            : undefined;

        if (isTotalRow && (!dataRow.totalCost || dataRow.totalCost === '')) {
            return null;
        }

        return (
            <tr key={rowKey} className={rowClasses}>
                {columnDefinitions.map(colDef => {
                    const isFirstColumn = colDef.key === firstColumnKey;
                    const cellClasses = isFirstColumn
                        ? 'fm-spc-fixed-column'
                        : '';

                    if (colDef.key === 'brand') {
                        return renderBrandColumn(
                            dataRow,
                            rowIndex,
                            brandOptions,
                            validBrands,
                            selectedBrand,
                            handleInternalRadioChange,
                            getDisplayName
                        );
                    }

                    const displayValue = getDisplayValue({
                        columnKey: colDef.key,
                        dataRow,
                        selectedBrand,
                        brandOptions,
                        validBrands,
                        formatWithThousandSeparators,
                        getCurrencySymbol,
                        currentAppCountry,
                    });

                    return (
                        <td key={colDef.key} className={cellClasses}>
                            {displayValue}
                        </td>
                    );
                })}
            </tr>
        );
    };

    const getDisplayValue = ({
        columnKey,
        dataRow,
        selectedBrand,
        brandOptions,
        validBrands,
        formatWithThousandSeparators,
        getCurrencySymbol,
        currentAppCountry,
    }: GetDisplayValueParams) => {
        if (columnKey === 'totalCost') {
            return getTotalCostDisplayValue(
                dataRow,
                selectedBrand,
                brandOptions,
                validBrands,
                formatWithThousandSeparators,
                getCurrencySymbol,
                currentAppCountry
            );
        }

        if (
            columnKey === 'unitUsed' &&
            (dataRow.description === 'Consumables' ||
                dataRow.description === 'Consumables*')
        ) {
            return '';
        }

        return String(dataRow[columnKey] ?? '');
    };

    const getTotalCostDisplayValue = (
        dataRow: DataRow,
        selectedBrand: string,
        brandOptions: string[] | undefined,
        validBrands: string[],
        formatWithThousandSeparators: (value: number) => string,
        getCurrencySymbol: () => string,
        currentAppCountry: string
    ) => {
        if (!brandOptions || brandOptions.length === 0 || !selectedBrand) {
            return formatCost(
                dataRow.totalCost,
                dataRow.excludeCurrencySymbol,
                formatWithThousandSeparators,
                getCurrencySymbol,
                currentAppCountry
            );
        }

        const isFordSelected = selectedBrand === validBrands[0];
        const isMotorcraftSelected = selectedBrand === validBrands[1];

        if (isFordSelected) {
            return formatCost(
                dataRow.totalCost,
                dataRow.excludeCurrencySymbol,
                formatWithThousandSeparators,
                getCurrencySymbol,
                currentAppCountry
            );
        }

        if (isMotorcraftSelected) {
            return formatCost(
                dataRow.otherPartCost,
                dataRow.excludeCurrencySymbol,
                formatWithThousandSeparators,
                getCurrencySymbol,
                currentAppCountry
            );
        }

        return '';
    };

    const formatCost = (
        cost: string | number | undefined,
        excludeCurrencySymbol: boolean | undefined,
        formatWithThousandSeparators: (value: number) => string,
        getCurrencySymbol: () => string,
        currentAppCountry: string
    ) => {
        if (cost === undefined || cost === null) return '';

        if (typeof cost === 'string' && cost.toLowerCase() === 'free') {
            return 'Free';
        }

        const numericValue =
            typeof cost === 'number' ? cost : parseFloat(String(cost));
        if (isNaN(numericValue)) return '';

        const formattedValue = formatWithThousandSeparators(numericValue);

        if (excludeCurrencySymbol) {
            return formattedValue;
        }

        if (currentAppCountry.toUpperCase() === 'THA') {
            return `${formattedValue} ${getCurrencySymbol()}`;
        }

        return `${getCurrencySymbol()} ${formattedValue}`;
    };

    const renderTableRows = ({
        rows,
        columnDefinitions,
        firstColumnKey,
        validBrands,
        internalSelectedBrands,
        handleInternalRadioChange,
        getDisplayName,
        formatWithThousandSeparators,
        getCurrencySymbol,
        currentAppCountry,
    }: RenderTableRowsParams) => {
        return rows.map((row, rowIndex) => {
            const rowKey = `row-${rowIndex}`;
            const selectedBrand = internalSelectedBrands[rowIndex] ?? '';

            if (row.type === 'header') {
                return renderHeaderRow(
                    rowKey,
                    row,
                    columnDefinitions,
                    firstColumnKey
                );
            }

            if (row.type === 'title') {
                return renderTitleRow(rowKey, row, columnDefinitions);
            }

            if (row.type === 'data') {
                return renderDataRow({
                    rowKey,
                    dataRow: row,
                    rowIndex,
                    columnDefinitions,
                    firstColumnKey,
                    validBrands,
                    selectedBrand,
                    handleInternalRadioChange,
                    getDisplayName,
                    formatWithThousandSeparators,
                    getCurrencySymbol,
                    currentAppCountry,
                });
            }

            return null;
        });
    };

    return (
        <>
            <div className="fm-spc-table-container">
                <table className="fm-spc-table fm-spc-fixed-layout">
                    <colgroup>
                        {columnDefinitions.map(colDef => (
                            <col
                                key={colDef.key}
                                className={
                                    colDef.fixed ? 'fm-spc-col-width' : ''
                                }
                                style={{ minWidth: colDef.width }}
                            />
                        ))}
                    </colgroup>
                    <tbody>
                        {renderTableRows({
                            rows,
                            columnDefinitions,
                            firstColumnKey,
                            validBrands,
                            internalSelectedBrands,
                            handleInternalRadioChange,
                            getDisplayName,
                            formatWithThousandSeparators,
                            getCurrencySymbol,
                            currentAppCountry,
                        })}
                    </tbody>
                </table>
            </div>

            {serviceSchedulePdf && (
                <p className="periodic-maintenance">
                    <div>
                        <a
                            href={serviceSchedulePdf.pdfUrl}
                            onClick={async e => {
                                e.preventDefault();
                                const response = await fetch(
                                    serviceSchedulePdf.pdfUrl
                                );
                                const blob = await response.blob();
                                const url = URL.createObjectURL(blob);

                                const link = document.createElement('a');
                                link.href = url;
                                link.download =
                                    serviceSchedulePdf.pdfUrl
                                        .split('/')
                                        .pop() ?? 'service-schedule.pdf';

                                document.body.appendChild(link);
                                link.click();
                                link.remove();

                                URL.revokeObjectURL(url);
                            }}
                        >
                            {zaLabel?.downloadPdfServiceScheduleHyperLinkText}
                        </a>
                        <span>
                            {
                                zaLabel?.downloadPdfServiceScheduleHyperLinkAdditionaltext
                            }
                        </span>
                    </div>
                </p>
            )}
        </>
    );
};

export default DynamicTableSPC;
